import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Image,
  Animated,
  Dimensions,
  Platform,
  ActivityIndicator,
  Pressable,
  Linking,
  ScrollView,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import { getColors } from '../styles/theme';
import { useIconTheme } from '../contexts/IconThemeContext';
import { getHotspotById, getHotspotReviews, getNearbyUsers, getRecurringEvents } from '../services/database';
import { getRecentSocialPosts } from '../services/socialSyncService';
import WriteReviewModal from './WriteReviewModal';
import { getProfilePictureUrl, getHotspotPhotoUrl } from '../services/storage';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

const HotspotDetailModal = ({ 
  visible, 
  hotspot: initialHotspot, 
  hotspotId,
  clubId,
  onClose, 
  navigation,
  // Position of the popup for animation origin
  popupPosition = { x: SCREEN_WIDTH / 2, y: SCREEN_HEIGHT / 2 }
}) => {
  const { user, appUser } = useAuth();
  const { showToast } = useToast();
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const { getCategoryIcon } = useIconTheme();
  const styles = getStyles(colors, isDarkMode);
  
  const [hotspot, setHotspot] = useState(initialHotspot || null);
  const [loading, setLoading] = useState(!initialHotspot);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [avgRating, setAvgRating] = useState(0);
  const [currentUserDbId, setCurrentUserDbId] = useState(null);
  const [showWriteReview, setShowWriteReview] = useState(false);
  const [nearbyPlayers, setNearbyPlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [recentSocialPosts, setRecentSocialPosts] = useState([]);
  const [socialPostsPlatform, setSocialPostsPlatform] = useState(undefined);
  const [loadingSocial, setLoadingSocial] = useState(false);
  const [recurringSchedules, setRecurringSchedules] = useState([]);
  const [loadingRecurring, setLoadingRecurring] = useState(false);

  // Sync hotspot prop with state when it changes or modal opens
  useEffect(() => {
    if (visible) {
      if (initialHotspot) {
        setHotspot(initialHotspot);
        setLoading(false);
      } else if (hotspotId) {
        setLoading(true);
        // Will fetch in the other useEffect
      }
      // Reset photo index when opening with new hotspot
      setActivePhotoIndex(0);
    }
  }, [visible, initialHotspot, hotspotId]);
  
  // Animation values
  const scaleAnim = useRef(new Animated.Value(0)).current;
  const opacityAnim = useRef(new Animated.Value(0)).current;
  const backdropAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (visible) {
      // Animate in
      Animated.parallel([
        Animated.spring(scaleAnim, {
          toValue: 1,
          tension: 65,
          friction: 10,
          useNativeDriver: true,
        }),
        Animated.timing(opacityAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(backdropAnim, {
          toValue: 1,
          duration: 250,
          useNativeDriver: true,
        }),
      ]).start();
      
      // If we weren't given a full hotspot object, fetch it by id.
      // For map flows we already have enough hotspot data passed in;
      // social posts now rely primarily on clubId/claimed_by instead
      // of the owner join, so this fetch is best-effort.
      if (!initialHotspot && hotspotId) {
        fetchHotspotDetails();
      }
      fetchCurrentUserDbId();
      fetchReviews();
      fetchNearbyPlayers();
    } else {
      // Reset animations
      scaleAnim.setValue(0);
      opacityAnim.setValue(0);
      backdropAnim.setValue(0);
    }
  }, [visible, hotspotId]);

  // Fetch recent social posts for the owning club
  const fetchSocialPosts = async () => {
    try {
      const displayHotspot = hotspot || initialHotspot;
      const ownerUserId =
        clubId ||
        (displayHotspot && displayHotspot.owner && displayHotspot.owner.id) ||
        displayHotspot?.claimed_by ||
        displayHotspot?.claimedBy ||
        displayHotspot?.owner_id;

      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/b52ea7f9-772d-4699-bf57-9767c6a601fb',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'0bf1ae'},body:JSON.stringify({sessionId:'0bf1ae',location:'HotspotDetailModal.js:fetchSocialPosts',message:'fetchSocialPosts inputs',data:{clubIdProp:clubId,claimed_by:displayHotspot?.claimed_by,claimedBy:displayHotspot?.claimedBy,owner_id:displayHotspot?.owner_id,ownerId:displayHotspot?.owner?.id,ownerUserId},timestamp:Date.now(),hypothesisId:'A'})}).catch(()=>{});
      // #endregion

      if (!ownerUserId) {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/b52ea7f9-772d-4699-bf57-9767c6a601fb',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'0bf1ae'},body:JSON.stringify({sessionId:'0bf1ae',location:'HotspotDetailModal.js:fetchSocialPosts',message:'no ownerUserId, skipping fetch',data:{clubIdProp:clubId},timestamp:Date.now(),hypothesisId:'B'})}).catch(()=>{});
        // #endregion
        setRecentSocialPosts([]);
        setSocialPostsPlatform(undefined);
        return;
      }

      setLoadingSocial(true);
      const { posts, platform } = await getRecentSocialPosts(ownerUserId, 6);
      setRecentSocialPosts(posts || []);
      setSocialPostsPlatform(platform);

      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/b52ea7f9-772d-4699-bf57-9767c6a601fb',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'0bf1ae'},body:JSON.stringify({sessionId:'0bf1ae',location:'HotspotDetailModal.js:fetchSocialPosts',message:'getRecentSocialPosts result',data:{ownerUserId,postsLength:(posts||[]).length},timestamp:Date.now(),hypothesisId:'D'})}).catch(()=>{});
      // #endregion
    } catch (e) {
      console.log('No social posts for hotspot/club or error:', e.message);
      setRecentSocialPosts([]);
    } finally {
      setLoadingSocial(false);
    }
  };

  // Re-run social fetch whenever we have a more complete hotspot object
  // or when clubId becomes available from props.
  useEffect(() => {
    if (!visible) return;
    const displayHotspot = hotspot || initialHotspot;
    if (!displayHotspot) return;
    fetchSocialPosts();
  }, [visible, initialHotspot, hotspot, clubId]);

  // Fetch recurring schedules when hotspot is claimed by a club
  useEffect(() => {
    if (!visible) return;
    const displayHotspot = hotspot || initialHotspot;
    const ownerUserId =
      clubId ||
      (displayHotspot && displayHotspot.owner && displayHotspot.owner.id) ||
      displayHotspot?.claimed_by ||
      displayHotspot?.claimedBy ||
      displayHotspot?.owner_id;
    if (!ownerUserId) {
      setRecurringSchedules([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoadingRecurring(true);
      try {
        const data = await getRecurringEvents(ownerUserId);
        if (!cancelled) setRecurringSchedules((data || []).filter(s => s.is_active !== false));
      } catch (e) {
        if (!cancelled) setRecurringSchedules([]);
      } finally {
        if (!cancelled) setLoadingRecurring(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [visible, clubId, hotspot, initialHotspot]);

  const handleClose = () => {
    // Animate out
    Animated.parallel([
      Animated.timing(scaleAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(opacityAnim, {
        toValue: 0,
        duration: 150,
        useNativeDriver: true,
      }),
      Animated.timing(backdropAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start(() => {
      onClose();
    });
  };

  const fetchHotspotDetails = async () => {
    try {
      setLoading(true);
      const data = await getHotspotById(hotspotId);
      setHotspot(data);
    } catch (error) {
      console.error('Error fetching hotspot details:', error);
      showToast('Failed to load hotspot details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUserDbId = async () => {
    if (appUser?.id) {
      setCurrentUserDbId(appUser.id);
    }
  };

  const fetchReviews = async () => {
    const targetId = hotspotId || initialHotspot?.id;
    if (!targetId) return;
    
    try {
      setLoadingReviews(true);
      
      const fetchedReviews = await getHotspotReviews(targetId, 20);
      
      const reviewsWithUrls = fetchedReviews.map((review) => {
        if (review.user?.profilePictureUrl && !review.user.profilePictureUrl.startsWith('http')) {
          const url = getProfilePictureUrl(review.user.profilePictureUrl);
          return { ...review, user: { ...review.user, profilePictureUrl: url } };
        }
        return review;
      });
      
      setReviews(reviewsWithUrls);
      if (reviewsWithUrls.length > 0) {
        const avg = reviewsWithUrls.reduce((sum, r) => sum + r.rating, 0) / reviewsWithUrls.length;
        setAvgRating(avg);
      } else {
        setAvgRating(0);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setReviews([]);
      setAvgRating(0);
    } finally {
      setLoadingReviews(false);
    }
  };

  const fetchNearbyPlayers = async () => {
    const targetHotspot = hotspot || initialHotspot;
    if (!targetHotspot?.latitude || !targetHotspot?.longitude) return;
    
    try {
      setLoadingPlayers(true);
      const allUsers = await getNearbyUsers(targetHotspot.latitude, targetHotspot.longitude, 0.5);
      
      const nearby = (allUsers || []).filter(u => {
        if (!u.latitude || !u.longitude || u.account_type === 'CLUB') return false;
        return true;
      });
      
      const playersWithUrls = nearby.slice(0, 10).map((player) => {
        const picUrl = player.profile_picture_url;
        if (picUrl && !picUrl.startsWith('http')) {
          return { ...player, profilePictureUrl: getProfilePictureUrl(picUrl), username: player.username };
        }
        return { ...player, profilePictureUrl: picUrl, username: player.username };
      });
      
      setNearbyPlayers(playersWithUrls);
    } catch (error) {
      console.error('Error fetching nearby players:', error);
    } finally {
      setLoadingPlayers(false);
    }
  };

  const getDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      PARK: 'Park', CAFE: 'Cafe', BAR: 'Bar', CLUB: 'Chess Club',
      UNIVERSITY: 'University', PUBLIC_SQUARE: 'Public Square',
      LIBRARY: 'Library', OTHER: 'Other',
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      PARK: '#4CAF50', CAFE: '#FF9800', BAR: '#9C27B0', CLUB: '#2196F3',
      UNIVERSITY: '#F44336', PUBLIC_SQUARE: '#00BCD4', LIBRARY: '#795548', OTHER: '#607D8B',
    };
    return colors[category] || '#607D8B';
  };

  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const formatTime12 = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
  };
  const formatRecurringLine = (s) =>
    `Every ${DAYS[s.day_of_week] ?? 'Day'} ${formatTime12(s.start_time)}${s.end_time ? ` – ${formatTime12(s.end_time)}` : ''}${s.title ? ` (${s.title})` : ''}`;

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Icon key={i} name={i <= rating ? 'star' : 'star-border'} size={16} color="#FFD700" />
      );
    }
    return stars;
  };

  const handleReviewSubmitted = async (newReview) => {
    let reviewWithUrl = newReview;
    if (newReview.user?.profilePictureUrl && !newReview.user.profilePictureUrl.startsWith('http')) {
      const url = getProfilePictureUrl(newReview.user.profilePictureUrl);
      reviewWithUrl = { ...newReview, user: { ...newReview.user, profilePictureUrl: url } };
    }
    const updatedReviews = [reviewWithUrl, ...reviews];
    setReviews(updatedReviews);
    const avg = updatedReviews.reduce((sum, r) => sum + r.rating, 0) / updatedReviews.length;
    setAvgRating(avg);
    showToast('Review submitted successfully!', 'success');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (!visible) return null;

  // Use initialHotspot directly if available, otherwise use fetched hotspot
  // Prefer fetched hotspot when set so we have owner/claimed_by for social posts
  const displayHotspot = hotspot || initialHotspot;
  const rawPhotos = displayHotspot?.photos || [];
  const photos = rawPhotos.map((p) => (p && String(p).startsWith('http') ? p : getHotspotPhotoUrl(p))).filter(Boolean);
  const hasScheduleContent = displayHotspot?.schedule || (recurringSchedules && recurringSchedules.length > 0);
  
  // Only show loading if we don't have any hotspot data to display
  const showLoading = !displayHotspot && loading;

  return (
    <View style={styles.container}>
      {/* Animated Backdrop */}
      <Animated.View 
        style={[
          styles.backdrop,
          { opacity: backdropAnim.interpolate({ inputRange: [0, 1], outputRange: [0, 0.6] }) }
        ]}
      >
        <Pressable style={styles.backdropPress} onPress={handleClose} />
      </Animated.View>

      {/* Animated Modal */}
      <Animated.View
        style={[
          styles.modal,
          {
            opacity: opacityAnim,
            transform: [
              { scale: scaleAnim.interpolate({ inputRange: [0, 1], outputRange: [0.5, 1] }) },
            ],
          },
        ]}
      >
        {/* Close Button */}
        <TouchableOpacity style={styles.closeButton} onPress={handleClose}>
          <Icon name="close" size={24} color={colors.textSecondary} />
        </TouchableOpacity>

        {showLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : displayHotspot ? (
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator
          >
            {/* Header: Photo + Info */}
            <View style={styles.header}>
              {/* Photo */}
              <View style={styles.photoSection}>
                {photos.length > 0 ? (
                  <View style={styles.photoContainer}>
                    <Image source={{ uri: photos[activePhotoIndex] }} style={styles.photo} />
                    {photos.length > 1 && (
                      <View style={styles.photoNav}>
                        <TouchableOpacity
                          style={[styles.photoNavBtn, activePhotoIndex === 0 && styles.photoNavBtnDisabled]}
                          onPress={() => activePhotoIndex > 0 && setActivePhotoIndex(activePhotoIndex - 1)}
                          disabled={activePhotoIndex === 0}
                        >
                          <Icon name="chevron-left" size={20} color={activePhotoIndex === 0 ? colors.textLight : '#fff'} />
                        </TouchableOpacity>
                        <Text style={styles.photoCounter}>{activePhotoIndex + 1} / {photos.length}</Text>
                        <TouchableOpacity
                          style={[styles.photoNavBtn, activePhotoIndex === photos.length - 1 && styles.photoNavBtnDisabled]}
                          onPress={() => activePhotoIndex < photos.length - 1 && setActivePhotoIndex(activePhotoIndex + 1)}
                          disabled={activePhotoIndex === photos.length - 1}
                        >
                          <Icon name="chevron-right" size={20} color={activePhotoIndex === photos.length - 1 ? colors.textLight : '#fff'} />
                        </TouchableOpacity>
                      </View>
                    )}
                  </View>
                ) : (
                  <View style={styles.photoPlaceholder}>
                    <Text style={styles.photoPlaceholderIcon}>{getCategoryIcon(displayHotspot.category)}</Text>
                    <Text style={styles.photoPlaceholderText}>No photos</Text>
                  </View>
                )}
              </View>

              {/* Info */}
              <View style={styles.infoSection}>
                {/* Category and Tags Row */}
                <View style={styles.tagsRow}>
                  <View style={[styles.categoryBadge, { backgroundColor: getCategoryColor(displayHotspot.category) }]}>
                    <Text style={styles.categoryText}>{getCategoryLabel(displayHotspot.category)}</Text>
                  </View>
                  {displayHotspot.tags && displayHotspot.tags.length > 0 && displayHotspot.tags.map((tag, index) => (
                    <View key={index} style={styles.tagBadge}>
                      <Text style={styles.tagText}>
                        {tag.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </Text>
                    </View>
                  ))}
                </View>
                
                <View style={styles.titleRow}>
                  <Text style={styles.hotspotIcon}>{getCategoryIcon(displayHotspot.category)}</Text>
                  <Text style={styles.hotspotName}>{displayHotspot.name}</Text>
                </View>

                <View style={styles.ratingRow}>
                  <View style={styles.starsRow}>{renderStars(Math.round(avgRating))}</View>
                  <Text style={styles.ratingText}>
                    {reviews.length > 0 
                      ? `${avgRating.toFixed(1)} (${reviews.length} reviews)`
                      : 'No reviews yet'}
                  </Text>
                </View>

                {displayHotspot.city && (
                  <View style={styles.locationRow}>
                    <Icon name="location-city" size={16} color={colors.textSecondary} />
                    <Text style={styles.cityText}>{displayHotspot.city}</Text>
                  </View>
                )}
                {displayHotspot.address && (
                  <View style={styles.addressRow}>
                    <Icon name="place" size={16} color={colors.primary} />
                    <Text style={styles.addressText}>{displayHotspot.address}</Text>
                  </View>
                )}

                {displayHotspot.activityLevel && (
                  <View style={styles.activityBadge}>
                    <Icon name="trending-up" size={14} color={colors.warning} />
                    <Text style={styles.activityText}>{displayHotspot.activityLevel}</Text>
                  </View>
                )}

                {/* Social Links */}
                {(displayHotspot.instagram_url || displayHotspot.twitter_url || displayHotspot.facebook_url || displayHotspot.website_url) && (
                  <View style={styles.socialLinksRow}>
                    {displayHotspot.instagram_url && (
                      <TouchableOpacity 
                        style={styles.socialLink}
                        onPress={() => Platform.OS === 'web' ? window.open(displayHotspot.instagram_url, '_blank') : null}
                      >
                        <Icon name="camera-alt" size={18} color="#E4405F" />
                      </TouchableOpacity>
                    )}
                    {displayHotspot.twitter_url && (
                      <TouchableOpacity 
                        style={styles.socialLink}
                        onPress={() => Platform.OS === 'web' ? window.open(displayHotspot.twitter_url, '_blank') : null}
                      >
                        <Icon name="chat" size={18} color="#1DA1F2" />
                      </TouchableOpacity>
                    )}
                    {displayHotspot.facebook_url && (
                      <TouchableOpacity 
                        style={styles.socialLink}
                        onPress={() => Platform.OS === 'web' ? window.open(displayHotspot.facebook_url, '_blank') : null}
                      >
                        <Icon name="thumb-up" size={18} color="#1877F2" />
                      </TouchableOpacity>
                    )}
                    {displayHotspot.website_url && (
                      <TouchableOpacity 
                        style={styles.socialLinkWithUrl}
                        onPress={() => Platform.OS === 'web' ? window.open(displayHotspot.website_url, '_blank') : Linking.openURL(displayHotspot.website_url)}
                        activeOpacity={0.7}
                      >
                        <Icon name="language" size={18} color="#4CAF50" />
                        <Text style={styles.websiteUrlText} numberOfLines={1}>{displayHotspot.website_url}</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                )}
              </View>
            </View>

            {/* Description */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>About</Text>
              <Text style={styles.description}>{displayHotspot.description || 'No description available.'}</Text>
            </View>

            {/* Recent Social Posts */}
            {recentSocialPosts.length > 0 && (
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionTitle}>
                    {socialPostsPlatform === 'instagram'
                      ? 'Recent Posts from Instagram'
                      : socialPostsPlatform === 'facebook'
                        ? 'Recent Posts from Facebook'
                        : 'Recent Posts'}
                  </Text>
                  {socialPostsPlatform === 'instagram' && (
                    <View style={styles.platformBadge}>
                      <Icon name="camera-alt" size={14} color="#E4405F" />
                      <Text style={styles.platformBadgeText}>Instagram</Text>
                    </View>
                  )}
                  {socialPostsPlatform === 'facebook' && (
                    <View style={styles.platformBadge}>
                      <Icon name="thumb-up" size={14} color="#1877F2" />
                      <Text style={styles.platformBadgeText}>Facebook</Text>
                    </View>
                  )}
                </View>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.socialPostsRow}
                >
                  {recentSocialPosts.map((post) => {
                    let payload = post.raw_payload || {};
                    if (typeof payload === 'string') {
                      try {
                        payload = JSON.parse(payload);
                      } catch {
                        payload = {};
                      }
                    }
                    // For video/reel, use only thumbnail_url; media_url is a video URL and cannot be used in <Image>
                    const postType = (post.post_type || '').toString().toLowerCase();
                    const isVideo = postType === 'video' || postType === 'reels';
                    const imageUrl = isVideo
                      ? (payload && typeof payload.thumbnail_url === 'string' && payload.thumbnail_url) || null
                      : (payload && typeof payload.thumbnail_url === 'string' && payload.thumbnail_url) ||
                        post.media_url;

                    // #region agent log
                    fetch('http://127.0.0.1:7242/ingest/b52ea7f9-772d-4699-bf57-9767c6a601fb', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'X-Debug-Session-Id': '0bf1ae',
                      },
                      body: JSON.stringify({
                        sessionId: '0bf1ae',
                        location: 'HotspotDetailModal.js:RecentPostsRender',
                        message: 'rendering hotspot IG post',
                        data: {
                          postId: post.id,
                          postType: post.post_type,
                          hasRawPayload: !!post.raw_payload,
                          payloadHasThumbnail: !!(payload && payload.thumbnail_url),
                          payloadThumbnailSample:
                            payload && typeof payload.thumbnail_url === 'string'
                              ? payload.thumbnail_url.slice(0, 80)
                              : null,
                          mediaUrlSample:
                            typeof post.media_url === 'string' ? post.media_url.slice(0, 80) : null,
                          computedHasImageUrl: !!imageUrl,
                        },
                        timestamp: Date.now(),
                        hypothesisId: 'T1',
                      }),
                    }).catch(() => {});
                    // #endregion

                    return (
                      <TouchableOpacity
                        key={post.id}
                        style={styles.socialPostThumbWrap}
                        activeOpacity={0.8}
                        onPress={() =>
                          post.permalink &&
                          (Platform.OS === 'web'
                            ? window.open(post.permalink, '_blank')
                            : Linking.openURL(post.permalink))
                        }
                      >
                        <View style={styles.socialPostThumb}>
                          {imageUrl ? (
                            <Image source={{ uri: imageUrl }} style={styles.socialPostThumbImage} />
                          ) : (
                            <View style={styles.socialPostThumbPlaceholder}>
                              <Icon name={isVideo ? 'videocam' : 'image'} size={22} color={colors.textLight} />
                            </View>
                          )}
                        </View>
                        {post.caption ? (
                          <Text style={styles.socialPostCaption} numberOfLines={2}>
                            {post.caption}
                          </Text>
                        ) : null}
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              </View>
            )}

            {/* Schedule + Amenities */}
            <View style={styles.twoColumn}>
              <View style={styles.columnHalf}>
                <Text style={styles.sectionTitle}>Schedule</Text>
                {loadingRecurring ? (
                  <View style={styles.emptyState}>
                    <ActivityIndicator size="small" color={colors.primary} />
                  </View>
                ) : hasScheduleContent ? (
                  <View style={styles.scheduleCard}>
                    <Icon name="schedule" size={20} color={colors.primary} />
                    {displayHotspot.schedule ? (
                      <Text style={styles.scheduleText}>{displayHotspot.schedule}</Text>
                    ) : null}
                    {recurringSchedules.length > 0 && recurringSchedules.map((s, idx) => (
                      <Text key={s.id || idx} style={[styles.scheduleText, (idx > 0 || displayHotspot.schedule) ? { marginTop: 6 } : null]}>
                        {formatRecurringLine(s)}
                      </Text>
                    ))}
                  </View>
                ) : (
                  <View style={styles.emptyState}>
                    <Icon name="event-busy" size={28} color={colors.textLight} />
                    <Text style={styles.emptyTitle}>No schedule</Text>
                  </View>
                )}
              </View>

              <View style={styles.columnHalf}>
                <Text style={styles.sectionTitle}>Amenities</Text>
                {displayHotspot.amenities?.length > 0 ? (
                  <View style={styles.amenitiesGrid}>
                    {displayHotspot.amenities.map((amenity, index) => (
                      <View key={index} style={styles.amenityTag}>
                        <Icon name="check-circle" size={12} color={colors.primary} />
                        <Text style={styles.amenityText}>{amenity}</Text>
                      </View>
                    ))}
                  </View>
                ) : (
                  <View style={styles.emptyState}>
                    <Icon name="info-outline" size={28} color={colors.textLight} />
                    <Text style={styles.emptyTitle}>None listed</Text>
                  </View>
                )}
              </View>
            </View>

            {/* Club Owner */}
            {displayHotspot.owner && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Club Owner</Text>
                <TouchableOpacity
                  style={styles.ownerCard}
                  onPress={() => {
                    handleClose();
                    setTimeout(() => navigation?.navigate('UserDetail', { userId: displayHotspot.owner.id }), 300);
                  }}
                >
                  {displayHotspot.owner.profile_picture_url ? (
                    <Image
                      source={{ uri: displayHotspot.owner.profile_picture_url }}
                      style={styles.ownerAvatar}
                    />
                  ) : (
                    <View style={styles.ownerAvatarPlaceholder}>
                      <Icon name="business" size={24} color="#fff" />
                    </View>
                  )}
                  <View style={styles.ownerInfo}>
                    <Text style={styles.ownerName}>
                      {displayHotspot.owner.club_name || displayHotspot.owner.username}
                    </Text>
                    <Text style={styles.ownerUsername}>@{displayHotspot.owner.username}</Text>
                  </View>
                  <Icon name="chevron-right" size={20} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>
            )}

            {/* Players */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Players Nearby</Text>
              {loadingPlayers ? (
                <ActivityIndicator size="small" color={colors.primary} />
              ) : nearbyPlayers.length > 0 ? (
                <View style={styles.playersGrid}>
                  {nearbyPlayers.map((player) => (
                    <TouchableOpacity 
                      key={player.id} 
                      style={styles.playerCard}
                      onPress={() => {
                        handleClose();
                        setTimeout(() => navigation?.navigate('UserDetail', { userId: player.id }), 300);
                      }}
                    >
                      {player.profilePictureUrl ? (
                        <Image source={{ uri: player.profilePictureUrl }} style={styles.playerAvatar} />
                      ) : (
                        <View style={styles.playerAvatarPlaceholder}>
                          <Icon name="person" size={16} color={colors.primary} />
                        </View>
                      )}
                      <Text style={styles.playerName} numberOfLines={1}>{player.username}</Text>
                      {player.isOnline && <View style={styles.onlineDot} />}
                    </TouchableOpacity>
                  ))}
                </View>
              ) : (
                <View style={styles.emptyHighlight}>
                  <View style={styles.emptyIconCircle}>
                    <Icon name="group-add" size={24} color={colors.primary} />
                  </View>
                  <Text style={styles.emptyHighlightTitle}>Be the first to visit!</Text>
                  <Text style={styles.emptyHighlightText}>Head here to show up on the map</Text>
                </View>
              )}
            </View>

            {/* Reviews */}
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Reviews</Text>
                {currentUserDbId && (
                  <TouchableOpacity style={styles.writeReviewBtn} onPress={() => setShowWriteReview(true)}>
                    <Icon name="rate-review" size={14} color="#fff" />
                    <Text style={styles.writeReviewText}>Write Review</Text>
                  </TouchableOpacity>
                )}
              </View>
              
              {loadingReviews ? (
                <ActivityIndicator size="small" color={colors.primary} />
              ) : reviews.length > 0 ? (
                <View style={styles.reviewsList}>
                  {reviews.map((review) => (
                    <View key={review.id} style={styles.reviewCard}>
                      <View style={styles.reviewHeader}>
                        <TouchableOpacity 
                          style={styles.reviewerInfo}
                          onPress={() => {
                            if (review.user?.id) {
                              handleClose();
                              setTimeout(() => navigation?.navigate('UserDetail', { userId: review.user.id }), 300);
                            }
                          }}
                        >
                          {review.user?.profilePictureUrl ? (
                            <Image source={{ uri: getProfilePictureUrl(review.user.profilePictureUrl, 'thumbnail') || review.user.profilePictureUrl }} style={styles.reviewerAvatar} />
                          ) : (
                            <View style={styles.reviewerAvatarPlaceholder}>
                              <Icon name="person" size={12} color={colors.primary} />
                            </View>
                          )}
                          <View>
                            <Text style={styles.reviewerName}>{review.user?.username || 'Anonymous'}</Text>
                            <Text style={styles.reviewDate}>{formatDate(review.createdAt)}</Text>
                          </View>
                        </TouchableOpacity>
                        <View style={styles.starsRow}>{renderStars(review.rating)}</View>
                      </View>
                      {review.comment && <Text style={styles.reviewComment}>{review.comment}</Text>}
                    </View>
                  ))}
                </View>
              ) : (
                <View style={styles.emptyHighlight}>
                  <View style={[styles.emptyIconCircle, { backgroundColor: isDarkMode ? 'rgba(255, 215, 0, 0.2)' : '#FFF8E1' }]}>
                    <Icon name="star-outline" size={24} color="#FFD700" />
                  </View>
                  <Text style={styles.emptyHighlightTitle}>Be the first to review!</Text>
                  <Text style={styles.emptyHighlightText}>Share your experience</Text>
                  {currentUserDbId && (
                    <TouchableOpacity style={styles.emptyButton} onPress={() => setShowWriteReview(true)}>
                      <Icon name="rate-review" size={14} color="#fff" />
                      <Text style={styles.emptyButtonText}>Write a Review</Text>
                    </TouchableOpacity>
                  )}
                </View>
              )}
            </View>

            <View style={styles.bottomSpacer} />
          </ScrollView>
        ) : (
          <View style={styles.errorContainer}>
            <Icon name="error-outline" size={48} color={colors.textLight} />
            <Text style={styles.errorText}>Hotspot not found</Text>
          </View>
        )}
      </Animated.View>

      {/* Write Review Modal */}
      {displayHotspot && (
        <WriteReviewModal
          visible={showWriteReview}
          onClose={() => setShowWriteReview(false)}
          hotspotId={displayHotspot.id}
          hotspotName={displayHotspot.name}
          userId={currentUserDbId}
          onReviewSubmitted={handleReviewSubmitted}
        />
      )}
    </View>
  );
};

const getStyles = (colors, isDarkMode) => ({
  container: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  },
  backdrop: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#000',
  },
  backdropPress: {
    flex: 1,
  },
  modal: {
    backgroundColor: colors.surface,
    borderRadius: 24,
    width: '90%',
    maxWidth: 800,
    maxHeight: '85%',
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 20 },
    shadowOpacity: isDarkMode ? 0.5 : 0.35,
    shadowRadius: 40,
    elevation: 24,
  },
  closeButton: {
    position: 'absolute',
    top: 14,
    right: 14,
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: isDarkMode ? colors.border : colors.background,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  loadingContainer: {
    padding: 60,
    alignItems: 'center',
  },
  errorContainer: {
    padding: 60,
    alignItems: 'center',
  },
  errorText: {
    fontSize: 16,
    color: colors.textSecondary,
    marginTop: 12,
  },
  scrollView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    padding: 20,
    gap: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  photoSection: {
    width: 320,
    height: 240,
  },
  photoContainer: {
    width: '100%',
    height: '100%',
    borderRadius: 14,
    overflow: 'hidden',
    position: 'relative',
  },
  photo: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  photoNav: {
    position: 'absolute',
    bottom: 6,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
  },
  photoNavBtn: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  photoNavBtnDisabled: {
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
  photoCounter: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '600',
    textShadowColor: 'rgba(0,0,0,0.5)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  photoPlaceholder: {
    width: '100%',
    height: '100%',
    borderRadius: 14,
    backgroundColor: isDarkMode ? colors.backgroundDark : colors.background,
    justifyContent: 'center',
    alignItems: 'center',
  },
  photoPlaceholderIcon: {
    fontSize: 40,
    marginBottom: 4,
  },
  photoPlaceholderText: {
    fontSize: 12,
    color: colors.textSecondary,
  },
  infoSection: {
    flex: 1,
    paddingRight: 30,
  },
  categoryBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 10,
    marginBottom: 10,
  },
  categoryText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '600',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  hotspotIcon: {
    fontSize: 22,
  },
  hotspotName: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.text,
    flex: 1,
  },
  ratingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 10,
  },
  starsRow: {
    flexDirection: 'row',
  },
  ratingText: {
    fontSize: 12,
    color: colors.textSecondary,
  },
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginBottom: 3,
  },
  cityText: {
    fontSize: 13,
    color: colors.textSecondary,
    fontWeight: '500',
  },
  addressRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 5,
    marginBottom: 6,
  },
  addressText: {
    fontSize: 12,
    color: colors.textSecondary,
    flex: 1,
    lineHeight: 16,
  },
  activityBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: isDarkMode ? 'rgba(255, 193, 7, 0.2)' : '#fff8e1',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  activityText: {
    fontSize: 11,
    color: colors.text,
    fontWeight: '500',
  },
  tagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 10,
  },
  tagBadge: {
    backgroundColor: isDarkMode ? colors.border : colors.background,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.border,
  },
  tagText: {
    color: colors.textSecondary,
    fontSize: 10,
    fontWeight: '500',
  },
  socialLinksRow: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 10,
  },
  socialLink: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: isDarkMode ? colors.border : colors.background,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
  },
  socialLinkWithUrl: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: isDarkMode ? colors.border : colors.background,
    borderWidth: 1,
    borderColor: colors.border,
    flex: 1,
    minWidth: 0,
  },
  websiteUrlText: {
    fontSize: 12,
    color: '#4CAF50',
    flex: 1,
    textDecorationLine: 'underline',
  },
  socialPostsRow: {
    flexDirection: 'row',
    paddingHorizontal: 2,
  },
  socialPostThumbWrap: {
    width: 160,
    marginRight: 12,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }
      : { elevation: 3, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: 6 }),
  },
  socialPostThumb: {
    width: 160,
    height: 200,
    borderRadius: 0,
    overflow: 'hidden',
    backgroundColor: isDarkMode ? '#2A2A2A' : '#F0F0F0',
  },
  socialPostThumbImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  socialPostThumbPlaceholder: {
    width: 160,
    height: 200,
    justifyContent: 'center',
    alignItems: 'center',
  },
  socialPostCaption: {
    fontSize: 11,
    color: colors.textSecondary,
    paddingHorizontal: 10,
    paddingTop: 8,
    paddingBottom: 10,
    lineHeight: 16,
  },
  section: {
    padding: 20,
    paddingTop: 16,
    paddingBottom: 0,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 10,
  },
  platformBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
  },
  platformBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  description: {
    fontSize: 13,
    color: colors.textSecondary,
    lineHeight: 20,
  },
  twoColumn: {
    flexDirection: 'row',
    padding: 20,
    paddingTop: 16,
    gap: 20,
  },
  columnHalf: {
    flex: 1,
  },
  scheduleCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    backgroundColor: isDarkMode ? colors.backgroundDark : colors.background,
    borderRadius: 10,
    borderLeftWidth: 3,
    borderLeftColor: colors.primary,
  },
  scheduleText: {
    fontSize: 12,
    color: colors.text,
    flex: 1,
    lineHeight: 18,
  },
  amenitiesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  amenityTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 5,
    backgroundColor: isDarkMode ? 'rgba(127, 176, 105, 0.25)' : '#e8f5e9',
    borderRadius: 12,
  },
  amenityText: {
    fontSize: 11,
    color: colors.primary,
    fontWeight: '500',
  },
  emptyState: {
    alignItems: 'center',
    padding: 16,
    backgroundColor: isDarkMode ? colors.backgroundDark : colors.background,
    borderRadius: 10,
  },
  emptyTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.textSecondary,
    marginTop: 6,
  },
  playersGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  playerCard: {
    alignItems: 'center',
    width: 56,
    position: 'relative',
  },
  playerAvatar: {
    width: 42,
    height: 42,
    borderRadius: 21,
    marginBottom: 4,
  },
  playerAvatarPlaceholder: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: isDarkMode ? 'rgba(127, 176, 105, 0.3)' : '#e8f5e9',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 4,
  },
  playerName: {
    fontSize: 10,
    color: colors.text,
    fontWeight: '500',
    textAlign: 'center',
  },
  onlineDot: {
    position: 'absolute',
    top: 0,
    right: 6,
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.success,
    borderWidth: 2,
    borderColor: colors.surface,
  },
  emptyHighlight: {
    alignItems: 'center',
    padding: 24,
    backgroundColor: isDarkMode ? 'rgba(127, 176, 105, 0.15)' : '#f5fff5',
    borderRadius: 14,
    borderWidth: 2,
    borderColor: isDarkMode ? 'rgba(127, 176, 105, 0.4)' : '#e8f5e9',
    borderStyle: 'dashed',
  },
  emptyIconCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: isDarkMode ? 'rgba(127, 176, 105, 0.3)' : '#e8f5e9',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 10,
  },
  emptyHighlightTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.primary,
    marginBottom: 4,
  },
  emptyHighlightText: {
    fontSize: 12,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  ownerCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: isDarkMode ? colors.backgroundDark : colors.background,
    borderRadius: 12,
    padding: 12,
    gap: 12,
  },
  ownerAvatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: colors.border,
  },
  ownerAvatarPlaceholder: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  ownerInfo: {
    flex: 1,
  },
  ownerName: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 2,
  },
  ownerUsername: {
    fontSize: 12,
    color: colors.textSecondary,
  },
  emptyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: colors.primary,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 16,
    marginTop: 12,
  },
  emptyButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  writeReviewBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: colors.primary,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 14,
  },
  writeReviewText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '600',
  },
  reviewsList: {
    gap: 10,
  },
  reviewCard: {
    padding: 12,
    backgroundColor: isDarkMode ? colors.backgroundDark : colors.background,
    borderRadius: 10,
  },
  reviewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  reviewerInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  reviewerAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  reviewerAvatarPlaceholder: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: isDarkMode ? 'rgba(127, 176, 105, 0.3)' : '#e8f5e9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  reviewerName: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.text,
  },
  reviewDate: {
    fontSize: 10,
    color: colors.textSecondary,
    marginTop: 1,
  },
  reviewComment: {
    fontSize: 12,
    color: colors.textSecondary,
    lineHeight: 17,
  },
  bottomSpacer: {
    height: 20,
  },
});

export default HotspotDetailModal;

